import type { ApiKeyType, GeneratedApiKeyResponse } from '@orderlymeds/measurements-schemas';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	useArchiveProject,
	useDeactivateKey,
	useGenerateKey,
	useProject,
	useProjectKeys,
	useUpdateProject,
} from '../../api/projects';
import { useIsAdmin } from '../../auth/useAuth';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { ChangelogTimeline } from '../../components/ChangelogTimeline';
import { CopyButton } from '../../components/CopyButton';
import { InlineConfirm } from '../../components/InlineConfirm';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { Select } from '../../components/Select';
import { SkeletonLoader } from '../../components/SkeletonLoader';
import { StatusIndicator } from '../../components/StatusIndicator';
import { Table } from '../../components/Table';
import { useProjectBreadcrumbs } from '../../hooks/useProjectBreadcrumbs.js';
import { formatRelativeTime } from '../../utils/format';
import shared from '../shared.module.css';
import styles from './ProjectDetailPage.module.css';

export function ProjectDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const isAdmin = useIsAdmin();

	const { data: project, isLoading: projectLoading, error: projectError } = useProject(id ?? '');
	const { data: keys, isLoading: keysLoading } = useProjectKeys(id ?? '', isAdmin);

	const updateProject = useUpdateProject(id ?? '');
	const archiveProject = useArchiveProject(id ?? '');
	const generateKey = useGenerateKey(id ?? '');
	const deactivateKey = useDeactivateKey(id ?? '');
	const breadcrumbs = useProjectBreadcrumbs();

	// Settings form state
	const [settingsName, setSettingsName] = useState('');
	const [settingsOrigins, setSettingsOrigins] = useState('');
	const [settingsInitialized, setSettingsInitialized] = useState(false);
	const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

	// Deactivate confirm state per key
	const [deactivatingKeyId, setDeactivatingKeyId] = useState<string | null>(null);

	// Generate key state
	const [newKeyType, setNewKeyType] = useState<ApiKeyType>('client');
	const [generatedKey, setGeneratedKey] = useState<GeneratedApiKeyResponse | null>(null);

	// Initialize settings form once data loads
	if (project && !settingsInitialized) {
		setSettingsName(project.name);
		setSettingsOrigins(project.allowed_origins.join(', '));
		setSettingsInitialized(true);
	}

	const isSettingsDirty =
		project &&
		(settingsName.trim() !== project.name ||
			settingsOrigins
				.split(',')
				.map((s) => s.trim())
				.filter(Boolean)
				.join(',') !== project.allowed_origins.join(','));

	async function handleSaveSettings(e: React.FormEvent) {
		e.preventDefault();
		if (!project) return;
		const allowed_origins = settingsOrigins
			.split(',')
			.map((s) => s.trim())
			.filter(Boolean);
		await updateProject.mutateAsync({ name: settingsName.trim(), allowed_origins });
	}

	async function handleArchive() {
		await archiveProject.mutateAsync();
		navigate('/projects');
	}

	async function handleGenerateKey() {
		const created = await generateKey.mutateAsync({ type: newKeyType });
		setGeneratedKey(created);
	}

	async function handleDeactivateKey(keyId: string) {
		await deactivateKey.mutateAsync(keyId);
		setDeactivatingKeyId(null);
	}

	const keyColumns = [
		{ key: 'key_preview', label: 'Key', mono: true },
		{ key: 'type', label: 'Type' },
		{ key: 'status', label: 'Status' },
		{ key: 'created', label: 'Created' },
		{ key: 'actions', label: '' },
	];

	if (projectLoading) {
		return (
			<div>
				<PageHeader title="Loading…" breadcrumbs={breadcrumbs} />
			</div>
		);
	}

	if (projectError || !project) {
		return (
			<div>
				<PageHeader title="Project" breadcrumbs={breadcrumbs} />
				<Card>
					<p className={shared.errorMessage}>
						{projectError instanceof Error ? projectError.message : 'Project not found'}
					</p>
				</Card>
			</div>
		);
	}

	return (
		<div>
			<PageHeader
				title={project.name}
				breadcrumbs={breadcrumbs}
				actions={
					isAdmin ? (
						showArchiveConfirm ? (
							<InlineConfirm
								message={`Archive "${project.name}"?`}
								confirmLabel="Archive"
								destructive
								onConfirm={handleArchive}
								onCancel={() => setShowArchiveConfirm(false)}
							/>
						) : (
							<Button variant="destructive" onClick={() => setShowArchiveConfirm(true)}>
								Archive
							</Button>
						)
					) : undefined
				}
			/>

			<div className={shared.content}>
				{/* Settings Card */}
				<Card>
					<h2 className={shared.cardHeading}>Settings</h2>
					<form onSubmit={handleSaveSettings} className={styles.settingsForm}>
						<Input
							label="Name"
							name="settings-name"
							value={settingsName}
							onChange={setSettingsName}
							required
							disabled={!isAdmin}
						/>
						<Input
							label="Slug"
							name="settings-slug"
							value={project.slug}
							onChange={() => {}}
							mono
							disabled
						/>
						<Input
							label="Allowed Origins"
							name="settings-origins"
							value={settingsOrigins}
							onChange={setSettingsOrigins}
							placeholder="https://example.com, https://staging.example.com"
							disabled={!isAdmin}
						/>
						{updateProject.error && (
							<p className={shared.formError}>
								{updateProject.error instanceof Error
									? updateProject.error.message
									: 'Failed to save changes'}
							</p>
						)}
						{isAdmin && isSettingsDirty && (
							<div className={shared.formActions}>
								<Button variant="secondary" type="submit" loading={updateProject.isPending}>
									Save Changes
								</Button>
							</div>
						)}
					</form>
				</Card>

				{isAdmin && (
					<Card>
						<div className={styles.cardHeaderRow}>
							<h2 className={[shared.cardHeading, styles.cardHeaderHeading].join(' ')}>API Keys</h2>
							<div className={styles.generateKeyRow}>
								<Select
									label="Key type"
									name="key-type"
									value={newKeyType}
									onChange={(v) => setNewKeyType(v as ApiKeyType)}
									options={[
										{ value: 'client', label: 'Client' },
										{ value: 'server', label: 'Server' },
									]}
								/>
								<Button
									variant="secondary"
									onClick={handleGenerateKey}
									loading={generateKey.isPending}
								>
									Generate Key
								</Button>
							</div>
						</div>
						{generatedKey && (
							<div className={styles.generatedKeyPanel}>
								<p className={styles.generatedKeyMessage}>
									Save this key now. The full value is only shown once.
								</p>
								<div className={styles.generatedKeyRow}>
									<span className={styles.generatedKeyLabel}>
										{generatedKey.type === 'client' ? 'Client key' : 'Server key'}
									</span>
									<span className={styles.generatedKeyValue}>
										<CopyButton text={generatedKey.key} displayText={generatedKey.key} />
									</span>
								</div>
							</div>
						)}
						{generateKey.error && (
							<p className={shared.formError}>
								{generateKey.error instanceof Error
									? generateKey.error.message
									: 'Failed to generate key'}
							</p>
						)}
						<Table>
							<Table.Header columns={keyColumns} />
							<Table.Body>
								{keysLoading ? (
									<SkeletonLoader rows={3} columns={5} />
								) : !keys || keys.length === 0 ? (
									<tr>
										<td colSpan={5} className={styles.emptyCell}>
											No API keys yet.
										</td>
									</tr>
								) : (
									keys.map((apiKey) => (
										<Table.Row key={apiKey.id}>
											<Table.Cell mono>
												<span className={styles.truncatedKey}>{apiKey.key_preview}</span>
											</Table.Cell>
											<Table.Cell>{apiKey.type}</Table.Cell>
											<Table.Cell>
												<StatusIndicator status={apiKey.active ? 'active' : 'revoked'} />
											</Table.Cell>
											<Table.Cell mono>{formatRelativeTime(apiKey.created_at)}</Table.Cell>
											<Table.Cell>
												{apiKey.active &&
													(deactivatingKeyId === apiKey.id ? (
														<InlineConfirm
															message="Deactivate this key?"
															confirmLabel="Deactivate"
															destructive
															onConfirm={() => handleDeactivateKey(apiKey.id)}
															onCancel={() => setDeactivatingKeyId(null)}
														/>
													) : (
														<Button
															variant="secondary"
															onClick={() => setDeactivatingKeyId(apiKey.id)}
														>
															Deactivate
														</Button>
													))}
											</Table.Cell>
										</Table.Row>
									))
								)}
							</Table.Body>
						</Table>
					</Card>
				)}

				<Card>
					<h2 className={shared.cardHeading}>Activity</h2>
					<ChangelogTimeline projectId={id ?? ''} scope={{ include: ['project', 'api_key'] }} />
				</Card>
			</div>
		</div>
	);
}
